import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Phone, Eye, EyeOff, FileSpreadsheet, ArrowRight, Database, Check, AlertTriangle, Wifi, MapPin, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { formatarMoedaPlano } from '@/constants/planosCNPJ';
import { formatarTelefone, formatarMoeda } from '@/utils/formatters';
import { ClickToCall } from '@/components/common/ClickToCall';
import { useParqueMovelByCnpj, useParqueFixoByCnpj } from '@/hooks/useParqueVivoByCnpj';
import { useMapaParqueByCnpj } from '@/hooks/useMapaParqueByCnpj';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';
import type { PlantaVivoSnapshot } from '@/hooks/usePlantaVivoSnapshot';
import { useEffect, useMemo, useState } from 'react';

interface PlanoAtualPortabilidadeProps {
  lead: LeadCarteira;
  plantaVivoSnapshot: PlantaVivoSnapshot | null | undefined;
  proposta: {
    gb_atual: string;
    fibra_atual: string;
    tv_atual: string;
    linhas_atual?: string;
    valor_atual?: string;
  };
  onPropostaChange: (field: string, value: string) => void;
  ocultarPlanoAtual?: boolean;
  onToggleOcultar?: () => void;
  ocultarPlanoFixo?: boolean;
  onToggleOcultarFixo?: () => void;
  onIrParaFatura?: () => void;
  compactoMovel?: boolean;
  /** O lead tem a prévia de proposta do TOP: os cartões móvel e fixo não aparecem (Douglas 06/10/2026).
   *  O componente segue montado: é ele que preenche a proposta com os valores do parque. */
  semMovel?: boolean;
  semFixo?: boolean;
}

// Interface para agrupamento por conta (replicada do LeadParqueVivoTab)
interface ContaFixaAgrupada {
  conta_cobranca: string;
  linhas: string[];
  internet: {
    produto: string | null;
    tecnologia: string | null;
  } | null;
  valor_total: number;
  endereco: {
    logradouro: string | null;
    numero: string | null;
    bairro: string | null;
    cidade: string | null;
    uf: string | null;
  };
}

function agruparFixoPorConta(servicos: Array<{
  conta_cobranca: string;
  ds_produto: string;
  tecnologia_origem: string | null;
  vl_fat_bruto: number | null;
  designador: string | null;
  ds_tipo_produto: string | null;
  endereco: string | null;
  numero: string | null;
  bairro: string | null;
  ds_municipio_origem: string | null;
  uf: string | null;
}>): ContaFixaAgrupada[] {
  const contas = new Map<string, ContaFixaAgrupada>();
  
  for (const s of servicos) {
    const chave = s.conta_cobranca || 'sem_conta';
    
    if (!contas.has(chave)) {
      contas.set(chave, {
        conta_cobranca: s.conta_cobranca,
        linhas: [],
        internet: null,
        valor_total: 0,
        endereco: {
          logradouro: s.endereco,
          numero: s.numero,
          bairro: s.bairro,
          cidade: s.ds_municipio_origem,
          uf: s.uf,
        }
      });
    }
    
    const conta = contas.get(chave)!;
    conta.valor_total += s.vl_fat_bruto || 0;
    
    // Detectar internet (DS_TIPO_PRODUTO = 'DADOS')
    if (s.ds_tipo_produto === 'DADOS' && !conta.internet) {
      conta.internet = {
        produto: s.ds_produto,
        tecnologia: s.tecnologia_origem,
      };
    }
    
    // Coletar linhas telefônicas (designadores numéricos)
    if (s.designador && /^\d{10,11}$/.test(s.designador)) {
      if (!conta.linhas.includes(s.designador)) {
        conta.linhas.push(s.designador);
      }
    }
  }
  
  return Array.from(contas.values()).sort((a, b) => b.valor_total - a.valor_total);
}

export function PlanoAtualPortabilidade({ 
  lead, 
  plantaVivoSnapshot,
  proposta, 
  onPropostaChange, 
  ocultarPlanoAtual, 
  onToggleOcultar,
  ocultarPlanoFixo,
  onToggleOcultarFixo,
  onIrParaFatura,
  compactoMovel = false,
  semMovel = false,
  semFixo = false,
}: PlanoAtualPortabilidadeProps) {
  // Buscar dados diretamente do staging como fallback
  const { data: parqueMovel, isLoading: loadingStaging } = useParqueMovelByCnpj(lead?.cnpj);
  const { data: parqueFixo } = useParqueFixoByCnpj(lead?.cnpj);
  const { mapaParque } = useMapaParqueByCnpj(lead?.cnpj);
  
  const temSnapshot = !!plantaVivoSnapshot && plantaVivoSnapshot.total_linhas > 0;
  const temStagingData = parqueMovel && parqueMovel.length > 0;

  // Calcular totais do staging
  const stagingTotals = useMemo(() => {
    if (!parqueMovel || parqueMovel.length === 0) {
      return { linhas: 0, valor: 0, gb: 0, gbTrafegado: 0 };
    }
    return {
      linhas: parqueMovel.length,
      valor: parqueMovel.reduce((sum, l) => sum + (l.fat_medio_03_meses || 0), 0),
      gb: parqueMovel.reduce((sum, l) => sum + (l.qtd_gb_contratado_dados || 0), 0),
      gbTrafegado: parqueMovel.reduce((sum, l) => sum + (l.qtd_gb_traf_dados || 0), 0),
    };
  }, [parqueMovel]);

  // Usar dados do snapshot quando disponível, senão staging, senão proposta
  const totalLinhas = temSnapshot 
    ? plantaVivoSnapshot.total_linhas 
    : temStagingData
      ? stagingTotals.linhas
      : parseInt(proposta.linhas_atual || '0') || 0;
  
  const valorTotal = temSnapshot 
    ? plantaVivoSnapshot.valor_total 
    : temStagingData
      ? stagingTotals.valor
      : parseFloat(proposta.valor_atual?.replace(',', '.') || '0') || 0;
  
  const gbTotal = temSnapshot 
    ? plantaVivoSnapshot.gb_total 
    : temStagingData
      ? stagingTotals.gb
      : parseInt(proposta.gb_atual || '0') || 0;

  const linhasDetalhadas = plantaVivoSnapshot?.linhas_detalhadas || [];

  // 🆕 Auto-preencher campos da proposta quando tiver dados do staging (fallback)
  // ⛔ Só escreve valor que EXISTE. Antes, base sem GB/valor (ou sem linha) fazia
  // este efeito escrever "0" / "0,00" — que o próprio isFalsy considera vazio —
  // e ele reescrevia o mesmo valor a cada render, sem nunca fechar. Com o
  // callback do modal criando objeto novo a cada escrita, virava laço: tela
  // tremendo e "Maximum update depth". (Douglas 01/09/2026)
  useEffect(() => {
    if (!temStagingData || temSnapshot) return;
    const vazio = (val: string) => !val || val === '' || val === '0' || val === '0,00';
    const escrever = (campo: string, atual: string, novoValor: string) => {
      if (!vazio(atual) || vazio(novoValor)) return;
      onPropostaChange(campo, novoValor);
    };
    escrever('linhas_atual', proposta.linhas_atual || '', stagingTotals.linhas.toString());
    escrever('gb_atual', proposta.gb_atual || '', stagingTotals.gb.toString());
    escrever('valor_atual', proposta.valor_atual || '', stagingTotals.valor.toFixed(2).replace('.', ','));
  }, [temStagingData, temSnapshot, stagingTotals, proposta.linhas_atual, proposta.gb_atual, proposta.valor_atual, onPropostaChange]);

  const temDadosParaMostrar = temSnapshot || temStagingData;

  // Parque Fixo agrupado por conta
  const temFixo = parqueFixo && parqueFixo.length > 0;
  const contasFixasAgrupadas = useMemo(() => {
    if (!temFixo) return [];
    return agruparFixoPorConta(parqueFixo);
  }, [parqueFixo, temFixo]);

  const totalFaturaFixo = contasFixasAgrupadas.reduce((sum, c) => sum + c.valor_total, 0);
  const totalContas = contasFixasAgrupadas.length;
  const totalLinhasFixas = contasFixasAgrupadas.reduce((sum, c) => sum + c.linhas.length, 0);
  const totalLinksInternet = contasFixasAgrupadas.filter(c => c.internet).length;

  const temParqueFixoOuMapa = temFixo || (mapaParque?.qt_basica_linas && mapaParque.qt_basica_linas > 0);

  // §7 (Douglas 29/08) — a planta/base Vivo é a FONTE: os "Valores do parque"
  // nascem pré-preenchidos dela (editáveis, a planta é o default, não a prisão).
  // Dado que está no banco nunca é pedido de novo nem exibido como vazio.
  useEffect(() => {
    if (!temDadosParaMostrar) return;
    if (!proposta.linhas_atual && totalLinhas > 0) onPropostaChange('linhas_atual', String(totalLinhas));
    if (!proposta.gb_atual && gbTotal > 0) onPropostaChange('gb_atual', String(Math.round(gbTotal)));
    if (!proposta.valor_atual && valorTotal > 0) onPropostaChange('valor_atual', valorTotal.toFixed(2).replace('.', ','));
    // (as três guardas acima já cobrem o caso do valor zero: só escreve o que existe)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temDadosParaMostrar, totalLinhas, gbTotal, valorTotal]);

  return (
    <div className={semMovel && semFixo ? 'hidden' : 'space-y-4'}>
      {/* ═══════════════════════════ CARD MÓVEL ═══════════════════════════ */}
      {!semMovel && (
      <Card className={`overflow-hidden border-border bg-card ${ocultarPlanoAtual ? 'opacity-60' : ''}`}>
        <CardHeader className="border-b bg-[#FBFAF9] px-3.5 py-2 dark:bg-white/[0.02]">
          <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-card-foreground">
            <Phone className="h-3 w-3 text-muted-foreground" />
            Plano atual móvel · Vivo Empresas
            <Badge variant="outline" className="border-border bg-[#F2EEE8] text-[9px] font-bold uppercase text-muted-foreground dark:bg-white/[0.06]">
              Portabilidade
            </Badge>
            {temDadosParaMostrar && (
              <Badge variant="outline" className="border-border bg-card text-[9px] font-semibold lowercase text-muted-foreground" title="de onde vêm os dados do parque">
                {temSnapshot ? 'planta importada' : 'base Vivo'}
              </Badge>
            )}
            
            {/* Toggle para ocultar/mostrar na proposta */}
            {onToggleOcultar && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`ml-auto h-6 w-6 p-0 ${ocultarPlanoAtual ? 'text-primary' : 'text-primary'}`}
                      onClick={onToggleOcultar}
                    >
                      {ocultarPlanoAtual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p className="text-xs">
                      {ocultarPlanoAtual 
                        ? 'Plano Móvel OCULTO na proposta. Clique para mostrar.' 
                        : 'Clique para OCULTAR o plano móvel da proposta'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Loading state */}
          {loadingStaging && !temSnapshot && (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}

          {/* Se não tem nenhum dado, exibir alerta para importar */}
          {!loadingStaging && !temDadosParaMostrar ? (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-8 h-8 text-primary dark:text-[#C87484]" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary dark:text-[#C87484]">
                    Dados de Parque Vivo não encontrados
                  </p>
                  <p className="text-xs text-primary dark:text-[#C87484]">
                    Importe a Base Móvel na tela Master Leads ou o arquivo da Planta Vivo na aba FATURA.
                  </p>
                </div>
                {onIrParaFatura && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={onIrParaFatura}
                    className="border-border text-primary hover:bg-primary/5 dark:text-[#C87484]"
                  >
                    Ir para Fatura
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          ) : !loadingStaging && (
            <>
              {/* Resumo em cards - dados do snapshot OU staging */}
              <div className="mb-4 grid grid-cols-3 gap-2">
                <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Linhas</p>
                  <p className="font-display text-[20px] font-bold leading-tight tabular-nums text-foreground">{totalLinhas}</p>
                  <p className="text-[9.5px] text-muted-foreground/80">móveis</p>
                </div>
                <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Valor total</p>
                  <p className="truncate font-display text-[20px] font-bold leading-tight tabular-nums text-money-highlight">{formatarMoedaPlano(valorTotal)}</p>
                  <p className="text-[9.5px] text-muted-foreground/80">/mês</p>
                </div>
                <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">GB total</p>
                  <p className="font-display text-[20px] font-bold leading-tight tabular-nums text-foreground">{gbTotal}</p>
                  <p className="text-[9.5px] text-muted-foreground/80">GB</p>
                </div>
              </div>

              {/* Lista de linhas clicáveis do snapshot */}
              {!compactoMovel && linhasDetalhadas.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Linhas do Plano Vivo ({linhasDetalhadas.length})
                  </h4>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {linhasDetalhadas.slice(0, 20).map((linha, idx) => (
                      <ClickToCall
                        key={idx}
                        telefone={linha.telefone}
                      >
                        <Badge variant="outline" className="cursor-pointer border-border bg-card tabular-nums text-foreground hover:border-primary/40 hover:text-primary">
                          {formatarTelefone(linha.telefone)}
                        </Badge>
                      </ClickToCall>
                    ))}
                    {linhasDetalhadas.length > 20 && (
                      <Badge variant="secondary" className="text-xs">
                        +{linhasDetalhadas.length - 20} linhas
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Tabela Detalhada do Parque Móvel */}
              {!compactoMovel && temStagingData && parqueMovel && parqueMovel.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Database className="h-3 w-3 text-muted-foreground" />
                      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-card-foreground">
                        Detalhes do parque móvel ({parqueMovel.length} linhas)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {stagingTotals.gbTrafegado > 0 && (
                        <Badge variant="outline" className="text-[10px]">
                          Uso: {Math.round((stagingTotals.gbTrafegado / Math.max(stagingTotals.gb, 1)) * 100)}%
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs gap-1"
                        onClick={() => {
                          const nums = parqueMovel.map(l => formatarTelefone(l.nr_telefone || '')).filter(Boolean).join('\n');
                          if (!nums) return;
                          navigator.clipboard?.writeText(nums)
                            .then(() => toast.success(`${parqueMovel.length} telefone(s) copiado(s)`))
                            .catch(() => toast.error('Não foi possível copiar'));
                        }}
                      >
                        <Copy className="h-3 w-3" /> Copiar números
                      </Button>
                    </div>
                  </div>
                  <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <Table noWrapper>
                      <TableHeader>
                        <TableRow className="bg-muted hover:bg-muted">
                          <TableHead className="h-7 px-2 text-[8.5px] font-bold uppercase tracking-[0.08em]">Telefone</TableHead>
                          <TableHead className="h-7 px-2 text-[8.5px] font-bold uppercase tracking-[0.08em]">Plano</TableHead>
                          <TableHead className="h-7 px-2 text-right text-[8.5px] font-bold uppercase tracking-[0.08em]">Fatura</TableHead>
                          <TableHead className="h-7 px-2 text-center text-[8.5px] font-bold uppercase tracking-[0.08em]">M</TableHead>
                          <TableHead className="h-7 px-2 text-right text-[8.5px] font-bold uppercase tracking-[0.08em]">GB cont.</TableHead>
                          <TableHead className="h-7 px-2 text-right text-[8.5px] font-bold uppercase tracking-[0.08em]">GB uso</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parqueMovel.map((linha, idx) => {
                          const mLinha = linha.m ?? 0;
                          const semMultaLinha = mLinha >= 24;
                          return (
                            <TableRow key={idx} className="hover:bg-muted/50">
                              <TableCell className="text-xs p-2">
                                <ClickToCall telefone={linha.nr_telefone}>
                                  <span className="cursor-pointer font-bold tabular-nums text-foreground hover:text-primary hover:underline">
                                    {formatarTelefone(linha.nr_telefone)}
                                  </span>
                                </ClickToCall>
                              </TableCell>
                              <TableCell className="text-xs p-2 max-w-32 truncate" title={linha.plano || '-'}>
                                {linha.plano || '-'}
                              </TableCell>
                              <TableCell className="p-2 text-right text-xs font-semibold tabular-nums text-foreground">
                                {linha.fat_medio_03_meses ? formatarMoeda(linha.fat_medio_03_meses) : '—'}
                              </TableCell>
                              <TableCell className="p-2 text-center text-xs tabular-nums">
                                <span
                                  className={semMultaLinha ? 'font-semibold text-[#075E54] dark:text-[#25D366]' : 'font-semibold text-primary dark:text-[#C87484]'}
                                  title={mLinha > 0 ? (semMultaLinha ? `M${mLinha} — sem multa (M ≥ 24)` : `M${mLinha} — porta com multa (falta ${24 - mLinha} p/ M24)`) : 'M não informado'}
                                >
                                  {mLinha ? `M${mLinha}` : '—'}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs p-2 text-right text-muted-foreground">
                                {linha.qtd_gb_contratado_dados ? `${linha.qtd_gb_contratado_dados} GB` : '-'}
                              </TableCell>
                              <TableCell className="p-2 text-right text-xs tabular-nums text-muted-foreground">
                                {linha.qtd_gb_traf_dados ? `${linha.qtd_gb_traf_dados} GB` : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Campos editáveis (sempre visíveis para ajuste manual) */}
          <div className="border-t pt-2.5">
            <p className="mb-2 text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              {temDadosParaMostrar ? 'Valores do parque · ajuste se necessário' : 'Preencher após contato com cliente'}
            </p>
            <div className="grid grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Linhas</Label>
                <Input
                  type="number"
                  min="1"
                  value={proposta.linhas_atual || ''}
                  onChange={(e) => onPropostaChange('linhas_atual', e.target.value)}
                  placeholder="Ex: 26"
                  className="h-[30px] text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">GB Atual</Label>
                <Input
                  type="number"
                  value={proposta.gb_atual}
                  onChange={(e) => onPropostaChange('gb_atual', e.target.value)}
                  placeholder="Ex: 88"
                  className="h-[30px] text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Valor Total</Label>
                <Input
                  type="text"
                  value={proposta.valor_atual || ''}
                  onChange={(e) => onPropostaChange('valor_atual', e.target.value)}
                  placeholder="Ex: 906,77"
                  className="h-[30px] text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Tem Fibra?</Label>
                <Input
                  value={proposta.fibra_atual}
                  onChange={(e) => onPropostaChange('fibra_atual', e.target.value)}
                  placeholder="Não / 400MB..."
                  className="h-[30px] text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Tem TV?</Label>
                <Input
                  value={proposta.tv_atual || ''}
                  onChange={(e) => onPropostaChange('tv_atual', e.target.value)}
                  placeholder="Não / Compacto..."
                  className="h-[30px] text-xs"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* ═══════════════════════════ CARD FIXO (Separado) ═══════════════════════════ */}
      {temParqueFixoOuMapa && !semFixo && (
        <Card className={`overflow-hidden border-border bg-card ${ocultarPlanoFixo ? 'opacity-60' : ''}`}>
          <CardHeader className="border-b bg-[#FBFAF9] px-3.5 py-2 dark:bg-white/[0.02]">
            <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-card-foreground">
              <Phone className="h-3 w-3 text-muted-foreground" />
              Plano atual fixo · Vivo Empresas
              <Badge variant="outline" className="border-border bg-[#F2EEE8] text-[9px] font-bold uppercase text-muted-foreground dark:bg-white/[0.06]">
                Portabilidade
              </Badge>
              
              {/* Toggle para ocultar/mostrar fixo na proposta */}
              {onToggleOcultarFixo && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`ml-auto h-6 w-6 p-0 ${ocultarPlanoFixo ? 'text-muted-foreground' : 'text-muted-foreground'}`}
                        onClick={onToggleOcultarFixo}
                      >
                        {ocultarPlanoFixo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p className="text-xs">
                        {ocultarPlanoFixo 
                          ? 'Plano Fixo OCULTO na proposta. Clique para mostrar.' 
                          : 'Clique para OCULTAR o plano fixo da proposta'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {temFixo ? (
              <>
                {/* Resumo em cards */}
                <div className="mb-4 grid grid-cols-4 gap-2">
                  <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                    <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Contas</p>
                    <p className="font-display text-[20px] font-bold leading-tight tabular-nums text-foreground">{totalContas}</p>
                  </div>
                  <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                    <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Fatura total</p>
                    <p className="truncate font-display text-[20px] font-bold leading-tight tabular-nums text-money-highlight">{formatarMoeda(totalFaturaFixo)}</p>
                  </div>
                  <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                    <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Linhas fixas</p>
                    <p className="font-display text-[20px] font-bold leading-tight tabular-nums text-foreground">{totalLinhasFixas}</p>
                  </div>
                  <div className="rounded-[9px] border border-border bg-card px-3 py-2">
                    <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Banda larga</p>
                    <p className="font-display text-[20px] font-bold leading-tight tabular-nums text-foreground">{totalLinksInternet}</p>
                  </div>
                </div>

                {/* Cards agrupados por conta (estilo Complementar) */}
                <div className="space-y-3">
                  {contasFixasAgrupadas.map((conta) => (
                    <Card key={conta.conta_cobranca} className="border-l-4 border-l-[#6E1626] bg-card/80">
                      <CardContent className="p-3 space-y-2">
                        {/* Header da Conta */}
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium tabular-nums text-muted-foreground">
                            Conta {conta.conta_cobranca}
                          </span>
                          <span className="font-display text-sm font-bold tabular-nums text-foreground">
                            {formatarMoeda(conta.valor_total)}
                          </span>
                        </div>
                        
                        {/* Internet */}
                        <div className="flex items-center gap-2 text-sm">
                          <Wifi className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                          {conta.internet ? (
                            <span className="text-xs flex items-center gap-1.5 flex-wrap">
                              <strong className="font-medium">{conta.internet.produto}</strong>
                              {conta.internet.tecnologia && (
                                <Badge variant="outline" className="text-[10px] px-1">
                                  {conta.internet.tecnologia}
                                </Badge>
                              )}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">Sem internet</span>
                          )}
                        </div>
                        
                        {/* Linhas telefônicas clicáveis */}
                        {conta.linhas.length > 0 && (
                          <div className="flex items-center gap-2 text-sm flex-wrap">
                            <Phone className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                            {conta.linhas.map((telefone, idx) => (
                              <ClickToCall
                                key={idx}
                                telefone={telefone}
                                className="inline-flex cursor-pointer items-center gap-0.5 text-xs tabular-nums transition-colors hover:text-primary"
                              >
                                {formatarTelefone(telefone)}
                              </ClickToCall>
                            ))}
                          </div>
                        )}
                        
                        {/* Endereço */}
                        {(conta.endereco.logradouro || conta.endereco.cidade) && (
                          <div className="flex items-start gap-2 text-xs text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-1">
                              {[
                                conta.endereco.logradouro,
                                conta.endereco.numero,
                                conta.endereco.bairro,
                                conta.endereco.cidade,
                                conta.endereco.uf
                              ].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              /* Resumo simples do mapa quando não tem staging fixo */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-card rounded-lg text-center border border-border">
                    <span className="text-xs text-muted-foreground">Linhas Fixas</span>
                    <p className="text-xl font-bold text-muted-foreground">{mapaParque?.qt_basica_linas || 0}</p>
                  </div>
                  {mapaParque?.vl_car_fixa && mapaParque.vl_car_fixa > 0 && (
                    <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-center">
                      <span className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-primary dark:text-[#C87484]">Dívida fixa Vivo</span>
                      <p className="font-display text-xl font-bold tabular-nums text-primary dark:text-[#C87484]">
                        {formatarMoeda(mapaParque.vl_car_fixa)}
                      </p>
                    </div>
                  )}
                </div>
                {mapaParque?.vl_car_movel && mapaParque.vl_car_movel > 0 && (
                  <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-center">
                    <span className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-primary dark:text-[#C87484]">Dívida móvel Vivo</span>
                    <p className="font-display text-xl font-bold tabular-nums text-primary dark:text-[#C87484]">
                      {formatarMoeda(mapaParque.vl_car_movel)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
